import { useState } from "react";
import {
  REDACTION_CATEGORIES,
  SCORE_BANDS,
  SD_LANGUAGE,
  SUPPORTED_LANGUAGE_COUNT,
  SDPD,
} from "./data";
import { C, fmtInt } from "./theme";
import { Callout, Card, Eyebrow, Section, Source, Step, SubSection } from "./ui";

// ===================================================================
// 04. Capabilities
// ===================================================================

type Capability = {
  id: string;
  title: string;
  summary: string;
  detail: string[];
  reportTypes: string[];
};

const CAPABILITIES: Capability[] = [
  {
    id: "dictation",
    title: "Write the report by speaking it",
    summary:
      "The officer narrates what happened. The platform produces a structured report in their voice, in the agency's format.",
    detail: [
      "Record in the browser, on an iPhone or Android device, or in the car. Roughly three minutes of speaking produces a report that would take about 45 minutes to type.",
      "The officer's own phrasing, perspective, and judgment carry through. The platform organizes and formats; it does not invent.",
      "Dictate into an existing report at the cursor to add a paragraph, or rewrite a selected passage without starting over.",
    ],
    reportTypes: ["All report types"],
  },
  {
    id: "diarization",
    title: "Speaker separation on interviews and body-cam audio",
    summary:
      "Every speaker in a recording is separated and labeled, so statements are attributed correctly.",
    detail: [
      "The officer can name expected speakers before upload, or let the system identify them and rename afterward.",
      "Any segment can be reassigned to a different speaker, split, merged, retimed, or corrected.",
      "Speaker labels carry into the narrative, so the report attributes statements rather than blending them.",
    ],
    reportTypes: ["Crime", "Arrest", "Domestic violence", "Child abuse", "Homicide", "Follow-up"],
  },
  {
    id: "transcription",
    title: "Full transcription, included",
    summary:
      "Every recording is transcribed, editable, and exportable, at no additional charge and with no per-minute metering.",
    detail: [
      "Click any word to jump the audio to that moment. Correct anything the transcription got wrong.",
      "Regenerate the narrative from the corrected transcript, and the report follows the correction.",
      "Export the transcript separately as PDF or text, with a speaker roster, for discovery or case file.",
      "Transcription is not a metered add-on. Capturing more audio never increases the bill.",
    ],
    reportTypes: ["All report types"],
  },
  {
    id: "templates",
    title: "Agency report formats and officer preferences",
    summary:
      "Department-wide templates and per-officer style settings, pushed centrally so reports come out in SDPD's format.",
    detail: [
      "Agency administrators define report templates (headers, required structure, boilerplate, and drafting instructions) and push them to every officer.",
      "This is the mechanism that makes output conform to a specific report format, including ARJIS-9 officer-only reports.",
      "Per-officer settings cover perspective, date and time format, name and rank capitalization, badge number, and call sign handling, including per-shift call sign overrides.",
    ],
    reportTypes: ["Officer only (ARJIS-9)", "Crime", "Arrest", "Traffic collision"],
  },
  {
    id: "checklists",
    title: "Report-type checklists",
    summary:
      "Element-by-element prompts for report types where a missed element has consequences.",
    detail: [
      "Build a checklist per report type so the officer is walked through the elements before the report is drafted.",
      "Particularly suited to domestic violence, child abuse, and critical-incident reporting where statutory elements must be documented.",
      "Checklists are agency-defined and centrally managed.",
    ],
    reportTypes: ["Domestic violence", "Child abuse", "Homicide", "Death"],
  },
  {
    id: "statutes",
    title: "California statute identification",
    summary:
      "Relevant California code sections surfaced against the narrative, with one-click insertion.",
    detail: [
      "Set to California, the platform identifies applicable code sections from the content of the report.",
      "Each is presented with the code, its name, and a description; the officer chooses whether and where to insert it.",
      "Agency-uploaded statute and policy libraries are consulted alongside state code, so department-specific ordinances and internal references are available too.",
      "Statute output is a drafting aid presented with a disclaimer. It is not legal advice, and the officer remains responsible for charging decisions.",
    ],
    reportTypes: ["Arrest", "Crime", "Warrant affidavits"],
  },
  {
    id: "knowledge",
    title: "Agency policy and knowledge base",
    summary:
      "Upload department policies, statutes, and exemplar reports; the platform draws on them.",
    detail: [
      "Administrators upload policy manuals, general orders, statute libraries, and model reports.",
      "Content is indexed and consulted so officers get answers and citations grounded in SDPD's own documents rather than generic guidance.",
      "Exemplar reports can be used to generate report templates that match how the department already writes.",
    ],
    reportTypes: ["All report types"],
  },
  {
    id: "forms",
    title: "Fill agency PDF forms by voice",
    summary:
      "Upload any department PDF once, describe each field in plain English, then fill it by speaking.",
    detail: [
      "Fields are detected automatically from fillable PDFs, or placed visually on the page for scanned forms.",
      "Each field carries a plain-English description telling the platform what belongs in it.",
      "The officer dictates once and the whole form is populated, with a confidence score on every field so low-confidence entries get attention.",
      "Prior uploads attached to the submission are used as source material, so a form can be filled from the audio already captured on the case.",
      "Output as a filled PDF, as plain text, or as AI-written prose in a chosen style.",
    ],
    reportTypes: ["Traffic collision", "Missing persons", "Death", "Warrant affidavits"],
  },
  {
    id: "redactions",
    title: "Redaction with California statutory citations",
    summary:
      "Automatic identification of protected information, a manual editor, and a true destructive redaction on export.",
    detail: [
      `Thirteen categories are detected automatically: ${REDACTION_CATEGORIES.join(", ")}.`,
      "Each proposed redaction carries a California statutory basis and a justification, compiled into a numbered footnote appendix on the released document.",
      "A full editor lets the reviewer draw, move, and resize boxes, select text directly, tag roles, and enable or disable individual redactions.",
      "Export rasterizes every page and burns the redactions in, so the underlying text does not survive in the released file. This is a real redaction, not a black rectangle drawn over live text.",
      "Optical character recognition assets are self-hosted; nothing is sent to a third-party CDN during redaction.",
      "Every upload, edit, and export is written to the audit trail.",
    ],
    reportTypes: ["Public records release", "All report types"],
  },
  {
    id: "axon",
    title: "Axon Evidence integration",
    summary:
      "Pull body-worn camera footage straight from evidence.com without downloading it first.",
    detail: [
      "The department creates an API key once. After that, officers browse their own Axon evidence locker from inside the platform and select the recordings that belong to the case.",
      "Nothing is downloaded and nothing is re-uploaded. The transfer runs server-side between Axon and us, so no officer waits on a multi-gigabyte export, moves an evidence file onto a laptop, or creates a second copy of evidence outside the evidence management system.",
      "Transfers stream in the background, so the officer can close the browser and return to service while processing continues.",
      "Oversized video has its audio extracted automatically before transcription.",
      "Configured per agency with the department's own Axon API credentials, and tested before it is enabled.",
    ],
    reportTypes: ["All report types"],
  },
  {
    id: "platforms",
    title: "Web and native mobile",
    summary: "Full platform in the browser; record, upload, edit, and review on iOS and Android.",
    detail: [
      "The web application carries the complete feature set, including forms, redactions, and administration.",
      "Native iOS and Android apps let officers record dictation, upload or pull evidence, edit the report, run scoring, identify statutes, and review transcripts from the field.",
      "Work started on a phone continues on a desktop and vice versa. It is one case record.",
    ],
    reportTypes: ["All report types"],
  },
];

export function CapabilitiesSection() {
  const [open, setOpen] = useState<string | null>("dictation");

  return (
    <Section
      id="capabilities"
      number="04"
      title="What the platform does"
      subtitle={
        <>
          SDPD named twelve report types in the RFI: six written by patrol, six by
          investigators. The capabilities below are mapped against them. Select any
          capability to expand it.
        </>
      }
    >
      <div className="space-y-2">
        {CAPABILITIES.map((cap) => {
          const isOpen = open === cap.id;
          return (
            <div
              key={cap.id}
              className={`rounded-lg border transition-colors ${
                isOpen ? "border-blue-300 bg-blue-50/30" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : cap.id)}
                aria-expanded={isOpen}
                className="w-full text-left p-4 flex items-start gap-3"
              >
                <span
                  className={`shrink-0 mt-0.5 w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold transition-transform ${
                    isOpen ? "rotate-45" : ""
                  }`}
                  style={{ backgroundColor: isOpen ? C.primary : "#cbd5e1" }}
                  aria-hidden
                >
                  +
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-slate-900 leading-snug">
                    {cap.title}
                  </span>
                  <span className="block text-sm text-slate-600 leading-relaxed mt-1">
                    {cap.summary}
                  </span>
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pl-12">
                  <ul className="space-y-2 mb-4">
                    {cap.detail.map((d) => (
                      <li key={d} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                        <span
                          className="shrink-0 w-1.5 h-1.5 rounded-full mt-[7px]"
                          style={{ backgroundColor: C.primary }}
                        />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-1.5">
                    {cap.reportTypes.map((rt) => (
                      <span
                        key={rt}
                        className="text-[10px] uppercase tracking-wider font-bold text-slate-600 bg-white border border-slate-300 rounded px-1.5 py-0.5"
                      >
                        {rt}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SubSection
        title="A note on public records"
        subtitle="Redaction is usually treated as a back-office problem. In California it is a statutory deadline with a labor cost attached."
      >
        <Callout tone="legal">
          <p>
            Between the Public Records Act and the disclosure obligations that apply to
            officer-involved shootings, use-of-force incidents, and sustained findings of
            misconduct, SDPD releases records that must be reviewed and redacted line by
            line. That work is slow, it is done by people whose time is expensive, and an
            error in it is a privacy incident.
          </p>
          <p>
            The same platform that writes the report also redacts it, automatically
            identifying protected information, citing the California authority for each
            withholding, and producing a released file where the underlying text is
            gone rather than merely covered.
          </p>
        </Callout>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 05. Report quality
// ===================================================================

export function QualitySection() {
  return (
    <Section
      id="quality"
      number="05"
      title="A veteran supervisor in every officer's corner"
      subtitle={
        <>
          The slowest part of report writing is often not the writing. It is the loop:
          the officer submits, a sergeant reads it days later, it comes back for a missing
          element, and the officer reconstructs an incident they have half forgotten.
          Every pass through that loop costs both of them time.
        </>
      }
    >
      <SubSection
        title="Scoring, before it reaches a supervisor"
        subtitle="Every report is scored 0–100 against how completely it documents the incident, with specific, prioritized critiques."
      >
        <div className="grid md:grid-cols-[auto_minmax(0,1fr)] gap-6 items-start">
          <div className="rounded-lg border border-slate-200 p-4">
            <Eyebrow>Score bands</Eyebrow>
            <ul className="space-y-2">
              {SCORE_BANDS.map((b, i) => {
                const next = SCORE_BANDS[i - 1];
                const range = next ? `${b.min}–${next.min - 1}` : `${b.min}+`;
                return (
                  <li key={b.label} className="flex items-center gap-2.5 whitespace-nowrap">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: b.color }}
                    />
                    <span className="text-xs font-mono tabular-nums text-slate-500 w-12">
                      {range}
                    </span>
                    <span className="text-sm text-slate-700">{b.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <p className="text-sm text-slate-700 leading-relaxed">
              The score is not a grade handed to a supervisor. It is feedback given to
              the officer while they can still act on it. Alongside it sits a list of
              specific gaps: an element not documented, a detail left as a placeholder, a
              statement that needs attribution.
            </p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <Card title="Score">Where the report stands, and why.</Card>
              <Card title="Suggestions">What is missing, in priority order.</Card>
              <Card title="Re-score">Fix them and the score updates.</Card>
            </div>
          </div>
        </div>
      </SubSection>

      <SubSection
        title="Answer the critique out loud"
        subtitle="The officer does not have to work out where a missing detail belongs in the narrative. They answer the question by voice and review the proposed edits."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          <Step n={1} title="Read the suggestions">
            The report is scored and specific gaps are listed.
          </Step>
          <Step n={2} title="Record an answer">
            The officer speaks the missing details, with no typing and no hunting for the right
            paragraph.
          </Step>
          <Step n={3} title="Review each edit" color={C.accent}>
            Proposed changes are presented one at a time. The officer accepts or discards
            each. Nothing is inserted without approval.
          </Step>
          <Step n={4} title="Automatic re-score" color={C.good}>
            The report is scored again, so the officer sees the effect of what they just
            added.
          </Step>
        </div>

        <div className="mt-6">
          <Callout tone="good" title="Where the time actually goes">
            <p>
              A report kicked back for a missing element costs the officer the time to
              reopen it, the sergeant the time to read it twice, and the case the delay
              between the two. Catching the gap in the first ten minutes, while the
              incident is fresh, is worth more than the minutes it saves. It is the
              difference between a detail remembered and a detail reconstructed.
            </p>
          </Callout>
        </div>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 06. Language coverage
// ===================================================================

export function LanguagesSection() {
  return (
    <Section
      id="languages"
      number="06"
      title="San Diego does not conduct its interviews in English"
      subtitle={
        <>
          <strong className="text-slate-900">
            {SD_LANGUAGE.nonEnglishAtHomePct}% of San Diego residents
          </strong>{" "}
          ({fmtInt(SD_LANGUAGE.nonEnglishAtHomeCount)} people) speak a language other
          than English at home. For SDPD, multilingual capability is not a feature
          checkbox. It is a description of the job.
          <span className="block mt-2 text-[13px] text-slate-500">
            U.S. Census Bureau, American Community Survey 2024 1-year estimates, City of
            San Diego, table S1601 (Language Spoken at Home), population 5 years and over.
            Margin of error ±{SD_LANGUAGE.marginOfErrorPct} percentage points.
          </span>
        </>
      }
    >
      <LanguageChart />

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Card title="Interviews in the language they happened in">
          Declare the spoken languages on a recording, or let the system identify them
          automatically. A single recording can contain more than one language, so a
          bilingual interview transcribes without being split up first.
        </Card>
        <Card title="Officers dictate in their own language">
          An officer whose first language is not English can narrate their report in the
          language they think in. The department gets a report; the officer does not get
          a writing test.
        </Card>
      </div>

      <div className="mt-6">
        <Callout tone="legal" title="What SDPD spends on this today">
          <p>
            The FY2026 adopted budget carries a{" "}
            <strong>${fmtInt(SDPD.languageServicesContract)}</strong> contract line for
            sign language, translation, and transcription services. Transcription in{" "}
            {SUPPORTED_LANGUAGE_COUNT} languages is included in the platform with no
            per-minute charge.
          </p>
        </Callout>
      </div>

      <Source>
        Language data: {SD_LANGUAGE.source} (±{SD_LANGUAGE.marginOfErrorPct} pp). Budget
        line: {SDPD.source}, Figure G.
      </Source>
    </Section>
  );
}

function LanguageChart() {
  const langs = SD_LANGUAGE.topLanguages;
  const max = Math.max(...langs.map((l) => l.sharePct));

  return (
    <div className="rounded-lg border border-slate-200 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <Eyebrow>Most-spoken non-English languages at home, City of San Diego</Eyebrow>
        <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
          All {SUPPORTED_LANGUAGE_COUNT} supported languages include every one of these
        </span>
      </div>

      <div className="space-y-2.5">
        {langs.map((l) => (
          <div key={l.name} className="flex items-center gap-2 sm:gap-3">
            <span
              className="w-28 sm:w-52 shrink-0 text-xs sm:text-sm text-slate-700 truncate"
              title={l.name}
            >
              {l.name}
            </span>
            <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden min-w-0">
              <div
                className="h-full rounded-md transition-all duration-700 ease-out"
                style={{
                  width: `${Math.max((l.sharePct / max) * 100, 1.5)}%`,
                  backgroundColor: C.primary,
                }}
              />
            </div>
            <span className="w-14 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">
              {l.sharePct}%
            </span>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
        Shares are of total city population. Spanish dominates by a wide margin; the
        remainder is a long tail in which Tagalog, Vietnamese, Chinese, Arabic, Farsi, and
        Somali are the largest components. A vendor that supports "Spanish and English"
        covers the first bar and none of the rest.
      </p>
    </div>
  );
}
